/**
 * @module features/perf/usePerf
 *
 * React Query hooks for the Performance API (`/org/perf/*`) and the schedule rules
 * (`/org/automation/rules`) that run a saved test.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { extractUuid } from '@chatsdet/ids';
import type { PerfResult, PerfRunHeadlineDelta, PerfRunStatus, PerfVerdict } from '@chatsdet/types/perf';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type PerfTestListItem = {
  id: string;
  name: string;
  applicationId: string;
  environment: string;
  createdAt: string;
  updatedAt: string;
};

export type PerfRunListItem = {
  id: string;
  perfTestId: string | null;
  baseSha: string;
  headSha: string;
  status: PerfRunStatus;
  verdict: PerfVerdict | null;
  createdAt: string;
  headlineDeltas: PerfRunHeadlineDelta[];
};

export type PerfRunDetail = {
  id: string;
  perfTestId: string | null;
  applicationId: string;
  environment: string;
  baseSha: string;
  headSha: string;
  status: PerfRunStatus;
  verdict: PerfVerdict | null;
  result: PerfResult | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

type RunPage = { items: PerfRunListItem[]; nextCursor: string | null };

const PAGE_SIZE = 20;

export type PerfRunFilters = {
  perfTestId?: string;
  adHoc?: boolean;
  status?: PerfRunStatus;
  verdict?: PerfVerdict;
};

const fetchRunPage = async (params: PerfRunFilters & { limit: number; cursor?: string }): Promise<RunPage> => {
  const query = new URLSearchParams({ limit: String(params.limit) });
  if (params.perfTestId) query.set('perfTestId', params.perfTestId);
  if (params.adHoc) query.set('adHoc', 'true');
  if (params.status) query.set('status', params.status);
  if (params.verdict) query.set('verdict', params.verdict);
  if (params.cursor) query.set('cursor', params.cursor);
  const res = await fetch(`${API_URL}/org/perf/runs?${query}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, 'Failed to load performance runs');
  return ((await res.json()) as { data: RunPage }).data;
};

export function usePerfTests() {
  return useQuery({
    queryKey: ['perf', 'tests'],
    queryFn: async (): Promise<PerfTestListItem[]> => {
      const res = await fetch(`${API_URL}/org/perf/tests`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load performance tests');
      return ((await res.json()) as { data: PerfTestListItem[] }).data;
    },
  });
}

export function usePerfTest(testId: string) {
  return useQuery({
    queryKey: ['perf', 'test', testId],
    queryFn: async (): Promise<PerfTestListItem> => {
      const res = await fetch(`${API_URL}/org/perf/tests/${testId}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load performance test');
      return ((await res.json()) as { data: PerfTestListItem }).data;
    },
  });
}

/** Cursor-paged runs, newest first, narrowed by the server-side filters. */
export function usePerfRuns(filters: PerfRunFilters) {
  return useInfiniteQuery<RunPage, Error, InfiniteData<RunPage, string | undefined>, unknown[], string | undefined>({
    queryKey: ['perf', 'runs', filters],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    queryFn: ({ pageParam }) => fetchRunPage({ ...filters, limit: PAGE_SIZE, cursor: pageParam }),
  });
}

/** True once the org has any run at all, tied to a test or not; one row is enough to tell. Only asked once the test list came back empty. */
export function useHasPerfRuns(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['perf', 'has-runs'],
    queryFn: async () => (await fetchRunPage({ limit: 1 })).items.length > 0,
  });
}

/** Each saved test's newest run, so the catalog can show its last verdict. */
export function useLatestPerfRuns(testIds: string[]) {
  const results = useQueries({
    queries: testIds.map((perfTestId) => ({
      queryKey: ['perf', 'latest-run', perfTestId],
      queryFn: async () => (await fetchRunPage({ perfTestId, limit: 1 })).items[0] ?? null,
    })),
  });
  return new Map(testIds.map((id, index) => [id, results[index]?.data ?? null]));
}

export function usePerfRun(runId: string) {
  return useQuery({
    queryKey: ['perf', 'run', runId],
    // Poll while the run is still being measured.
    refetchInterval: (query) =>
      query.state.data && ['queued', 'running'].includes(query.state.data.status) ? 5_000 : false,
    queryFn: () => fetchPerfRun(runId),
  });
}

const fetchPerfRun = async (runId: string): Promise<PerfRunDetail> => {
  const res = await fetch(`${API_URL}/org/perf/runs/${runId}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, 'Failed to load performance run');
  return ((await res.json()) as { data: PerfRunDetail }).data;
};

type ScheduleRule = { enabled: boolean; actionConfig: { perf_test_id?: string } };

/** Ids (UUIDs) of the saved tests that have an enabled schedule rule. */
export function usePerfScheduledTestUuids() {
  return useQuery({
    queryKey: ['perf', 'schedules'],
    queryFn: async (): Promise<Set<string>> => {
      const res = await fetch(`${API_URL}/org/automation/rules?actionType=perf-test.run&pageSize=100`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load schedules');
      const rules = ((await res.json()) as { data: ScheduleRule[] }).data;
      return new Set(
        rules.flatMap((rule) => {
          const uuid = rule.enabled ? extractUuid(rule.actionConfig.perf_test_id) : null;
          return uuid ? [uuid] : [];
        })
      );
    },
  });
}

export function useStartPerfRun(perfTestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (head: string): Promise<{ id: string }> => {
      const res = await fetch(`${API_URL}/org/perf/runs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ perfTestId, head }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to start performance run');
      return ((await res.json()) as { data: { id: string } }).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['perf'] }),
  });
}
